import Button, { BUTTON_VARIANTS, ButtonVariantType } from "@shared/ui/Button";
import { HEADLINE_SIZES } from "@shared/ui/Headline";
import {
  SheetBody,
  SheetContent,
  SheetFooter,
  SheetHeader,
  SheetProvider,
  SheetTitle,
} from "@shared/ui/Sheet";
import React from "react";

/**
 * FormSheet - the common "title + form + Cancel/Save" panel.
 *
 * A composition of the Sheet primitives, not a wrapper around the legacy
 * Drawer: it shares none of Drawer's prop names, carries none of its dead props
 * (childrenSetRef / overlay / hideCloseButton / headerClassName, all with zero
 * usages), and deliberately offers NO headerComponent / footerComponent escape
 * hatches. Anything with custom chrome must use the Sheet primitives directly.
 *
 * The defaults reproduce the legacy Drawer's geometry and behaviour exactly, so
 * converting a call site changes nothing a user can see except that the panel
 * now slides in instead of appearing instantly.
 *
 * This API is frozen. New requirements go to the Sheet primitives, not here.
 */
export type FormSheetProps = {
  /** Controlled visibility. */
  open: boolean;
  /** Called for every dismissal path: header X, cancel button, Escape, overlay. */
  onClose: () => void;

  /** ReactNode rather than Drawer's `string`, so rich titles need no @ts-ignore. */
  title: React.ReactNode;
  children: React.ReactNode;

  side?: "left" | "right";
  /** Panel width. Matches Drawer's default, including its mobile override. */
  widthClassName?: string;
  /**
   * Stacking context. Defaults to Drawer's "z-10" rather than Sheet's "z-50",
   * so converted panels keep their existing place in the stack: AlertBox
   * (z-20) and Modal (hardcoded !z-10) still render above them, exactly as
   * they did before. Raise it only where a site already hand-tuned its own.
   */
  zIndexClass?: string;
  /**
   * Allow Escape and overlay click to dismiss. Defaults to false because the
   * legacy Drawer had neither, and enabling them on an unsaved form bypasses
   * that site's discard confirmation.
   */
  dismissible?: boolean;

  /**
   * Extra classes for the body (layout, gaps). APPENDED -- padding lives in
   * `bodyPadding`, so passing layout classes here never disturbs spacing.
   */
  bodyClassName?: string;
  /**
   * Body padding, REPLACING the default. Defaults to Drawer's exact geometry
   * (24px sides, 20px top/bottom). Override only to reproduce a site's own
   * padding, and spell it out in full -- Tailwind resolves shorthand vs
   * longhand by stylesheet order, not by the order of this string.
   */
  bodyPadding?: string;
  /** Extra classes for the footer. APPENDED -- see `footerPadding`. */
  footerClassName?: string;
  /** Footer padding, REPLACING the default (24px sides, 16px top/bottom). */
  footerPadding?: string;
  /** Omit the footer entirely (Drawer's `hideFooter`). */
  hideFooter?: boolean;

  cancelText?: string;
  onCancel?: () => void;
  cancelDisabled?: boolean;
  cancelVariant?: ButtonVariantType;
  submitText?: string;
  onSubmit?: () => void;
  submitDisabled?: boolean;
  submitLoading?: boolean;
};

export function FormSheet({
  open,
  onClose,
  title,
  children,
  side = "right",
  widthClassName = "w-[490px] max-md:w-full",
  zIndexClass = "z-10",
  dismissible = false,
  // Padding and layout are separate props on purpose. SheetBody renders
  // `p-2xl ${className}`, and Tailwind resolves shorthand vs longhand by
  // stylesheet order rather than string order -- so a single combined prop
  // either clobbers a caller's `!p-0` or silently shifts spacing when they
  // pass layout-only classes. Splitting them makes both cases exact.
  bodyClassName = "",
  bodyPadding = "!px-2xl !py-xl",
  footerClassName = "",
  footerPadding = "!px-2xl !py-lg",
  hideFooter = false,
  cancelText = "",
  onCancel = () => {},
  cancelDisabled = false,
  cancelVariant = BUTTON_VARIANTS.OUTLINE,
  submitText = "",
  onSubmit = () => {},
  submitDisabled = true,
  submitLoading = false,
}: FormSheetProps) {
  return (
    <SheetProvider
      open={open}
      onOpenChange={(next) => {
        if (!next) onClose();
      }}
      closeOnEscape={dismissible}
      closeOnOverlayClick={dismissible}
    >
      <SheetContent
        side={side}
        zIndexClass={zIndexClass}
        className={widthClassName}
      >
        <div className="flex flex-col h-full">
          <SheetHeader onClose={onClose}>
            <SheetTitle
              size={HEADLINE_SIZES.X_SMALL}
              className="break-all truncate"
            >
              {title}
            </SheetTitle>
          </SheetHeader>

          <SheetBody className={`${bodyPadding} ${bodyClassName}`}>
            {children}
          </SheetBody>

          {/*
            Rendered whenever the caller has not hidden it, even with no button
            text -- Drawer drew the bordered strip in that case and two call
            sites still show it. Removing it would be a visible change.
          */}
          {!hideFooter && (
            <SheetFooter className={`${footerPadding} ${footerClassName}`}>
              <div className="flex items-center justify-between gap-sm w-full">
                {cancelText && (
                  <Button
                    variant={cancelVariant}
                    text={cancelText}
                    className="flex-1"
                    onClick={onCancel}
                    disabled={cancelDisabled}
                  />
                )}

                {submitText && (
                  <Button
                    text={submitText}
                    variant={
                      submitLoading
                        ? BUTTON_VARIANTS.LOADING
                        : BUTTON_VARIANTS.PRIMARY
                    }
                    className="flex-1"
                    onClick={onSubmit}
                    disabled={submitDisabled}
                    data-testId="drawer-submit-button"
                  />
                )}
              </div>
            </SheetFooter>
          )}
        </div>
      </SheetContent>
    </SheetProvider>
  );
}

export default FormSheet;
